import React, {useEffect} from 'react';
import {Grid} from '@mui/material';
import {useLazyQuery} from '@apollo/client';
import AutoField, {ActionType} from '../../components/AutoForm/AutoFieldNew';
import {Button} from '../../components/Button/Button';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {
  ReportPackTemplateATRTypeEnum,
  ReportPackTemplateMonthsEnum,
} from '../../schema-types/globalTypes';
import {ReportPackFragment} from '../../schema-types/ReportPackFragment';
import {
  GetUncontactableLetterHtml,
  GetUncontactableLetterHtmlVariables,
} from '../../schema-types/GetUncontactableLetterHtml';
import {getUncontactableLetterHtmlQuery} from './reportPackQueries';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useReportPack} from './useReportPack';
import {UPDATES} from '../../shared/UPDATES';

interface UncontactableOptionsProps {
  reportPack: ReportPackFragment;
  onTemplateHtmlChange: (value: string) => void;
}

export const UncontactableOptions: React.FC<UncontactableOptionsProps> = ({
  reportPack,
  onTemplateHtmlChange,
}) => {
  const [{value: atrType}, _, {setValue: setAtrType}] =
    useAutoField<ReportPackTemplateATRTypeEnum>('atrType');
  const [{value: months}, __, {setValue: setMonths}] =
    useAutoField<ReportPackTemplateMonthsEnum>('months');
  const [fetchHtml] = useLazyQuery<
    GetUncontactableLetterHtml,
    GetUncontactableLetterHtmlVariables
  >(getUncontactableLetterHtmlQuery);
  const {reviewId} = useReviewContext();
  const {onUpsert} = useReportPack();

  const handleCreateTemplate = () => {
    if (!atrType || !months || !reportPack.templateType) {
      return;
    }
    fetchHtml({
      variables: {
        reportPackId: reportPack.id,
        formType: reportPack.templateType,
        atrType,
        months,
      },
    }).then(async (response) => {
      if (response.data && response.data.getUncontactableLetterHtml) {
        onTemplateHtmlChange(response.data.getUncontactableLetterHtml);
        await onUpsert({
          variables: {
            reviewId,
            reportPackId: reportPack.id,
            templateMonths: months,
            templateAtrType: atrType,
          },
        });
      }
    });
  };

  useEffect(() => {
    if (!atrType && reportPack.templateAtrType) {
      setAtrType(UPDATES._2106, reportPack.templateAtrType);
    }
    if (!months && reportPack.templateMonths) {
      setMonths(UPDATES._2107, reportPack.templateMonths);
    }
  }, [reportPack, atrType, months]);

  return (
    <>
      <Grid pt={2} pl={2} container spacing={3}>
        <Grid item xs={6} borderRight={1} borderColor="divider">
          <AutoField
            field={{
              id: 'atrType',
              label: 'ATR alignment',
              type: ActionType.radioGroup,
              horizontal: true,
              size: 'medium',
              fullWidth: true,
              boldLabel: true,
              horizontalOptionWidthXs: 4,
              options: [
                {
                  label: 'No ATR',
                  value: ReportPackTemplateATRTypeEnum.NO_ATR,
                },
                {
                  label: 'Aligned ATR',
                  value: ReportPackTemplateATRTypeEnum.ALIGNED_ATR,
                },
                {
                  label: 'Misaligned ATR',
                  value: ReportPackTemplateATRTypeEnum.MISALIGNED_ATR,
                },
              ],
            }}
          />
        </Grid>
        <Grid item xs={6} borderRight={1} borderColor="divider">
          <AutoField
            field={{
              id: 'months',
              label: 'Time period',
              boldLabel: true,
              type: ActionType.radioGroup,
              horizontal: true,
              horizontalOptionWidthXs: 6,
              fullWidth: true,
              options: [
                {
                  label: '12 months',
                  value: ReportPackTemplateMonthsEnum.TWELVE,
                },
                {
                  label: '24 months',
                  value: ReportPackTemplateMonthsEnum.TWENTYFOUR,
                },
              ],
            }}
          />
        </Grid>
      </Grid>
      <Button
        eventName="create-report-html"
        variant="contained"
        type="submit"
        sx={{marginTop: 1, ml: 2, mb: 2}}
        disabled={!atrType || !months}
        onClick={handleCreateTemplate}
      >
        Create template
      </Button>
    </>
  );
};
